import { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/consultation", label: "Consultation" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  const doctorName = settings?.doctor_name || "Dr Profile";

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAF8]">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/70" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <div className="w-9 h-9 rounded-xl bg-[#0F4C81] text-white grid place-items-center font-display font-bold text-sm">
              DP
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[15px] text-slate-900 whitespace-nowrap">Dr Profile</div>
              <div className="text-[11px] text-slate-500 -mt-0.5 hidden sm:block">{doctorName}</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-[#0F4C81]" : "text-slate-600 hover:text-slate-900"
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/doctor/login"
              className="inline-flex items-center gap-1.5 border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white font-semibold rounded-xl px-3 sm:px-4 py-1.5 text-sm transition-colors"
              data-testid="nav-doctor-login-btn"
            >
              <Stethoscope size={15} /> <span className="hidden sm:inline">Doctor Login</span><span className="sm:hidden">Doctor</span>
            </Link>
            <Link
              to="/lookup"
              className="hidden sm:inline-flex text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2"
              data-testid="nav-lookup"
            >
              Check booking
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl px-4 py-2 text-sm shadow-sm hover:shadow transition-all active:scale-[0.98]"
              data-testid="nav-book-appointment-btn"
            >
              <span className="hidden sm:inline whitespace-nowrap">Book Appointment</span><span className="sm:hidden">Book</span>
            </Link>
            <button
              className="md:hidden p-2 text-slate-700"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className="py-2 text-slate-700 font-medium">
                {n.label}
              </NavLink>
            ))}
            <NavLink to="/lookup" className="py-2 text-slate-700 font-medium">Check booking</NavLink>
          </div>
        )}
      </header>

      <main className="flex-1 fade-in">
        <Outlet context={{ settings }} />
      </main>

      <footer className="bg-[#0F172A] text-slate-300 mt-20" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
          <div>
            <div className="font-display font-extrabold text-white text-lg">Dr Profile</div>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              {settings?.tagline || "Trusted medical care, modern booking."}
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">Clinic</div>
            <p className="text-sm text-slate-300">{settings?.clinic_name}</p>
            <p className="text-sm text-slate-400 mt-1">{settings?.clinic_address}</p>
            <p className="text-sm text-slate-400">{settings?.clinic_city}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">Contact</div>
            {settings?.phone && (
              <a href={`tel:${settings.phone}`} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                <Phone size={14} /> {settings.phone}
              </a>
            )}
            {settings?.whatsapp && (
              <a
                href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-slate-300 hover:text-white mt-1"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">Legal</div>
            <Link to="/privacy" className="block text-sm text-slate-300 hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="block text-sm text-slate-300 hover:text-white mt-1">Terms &amp; Appointment Policy</Link>
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <div>© {new Date().getFullYear()} Dr Profile. All rights reserved.</div>
            <div>
              Developed by{" "}
              <a
                href="https://kaizenlabsco.com/"
                target="_blank" rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300 font-semibold"
                data-testid="kaizen-labs-footer-credit"
              >
                Kaizen Labs
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
